// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {get, isEmpty} from 'lodash';
import classnames from 'classnames';

// Components
import Button from '../core/button';
import Dropdown from '../core/dropdown';
import Permissions from '../core/permissions';

// Containers
import FlaggedMetadata from '../../containers/flagged-metadata';
import Mapping from '../../containers/mapping';
import withPermissions from '../../containers/with-permissions';

// Config
import echostarContentTypeMap from '../../bootstrap-data/echostar-content-type-map';

// Styles
import './mapping-button.scss';

function MappingButton({
  isShell,
  className,
  description,
  descriptionLanguage,
  disabled,
  echostarContentType,
  echostarId,
  episodeNumber,
  episodeSeason,
  genres,
  gnSeriesId,
  hasAccessByRole,
  originalAirDate,
  rating,
  releaseYear,
  roviProgramId,
  runtime,
  seriesId,
  seriesTitle,
  servicesDataKey,
  sourceId,
  title,
  titleLanguages,
  tmsId,
}) {
  const getButtonProps = (mapping) => ({
    className: showDropdown() ? classnames('mapping-button--button') : classnames('mapping-button', className),
    children: 'Map',
    disabled,
    onClick: (event) => handleClick(event, 'map', mapping),
  });

  const getContentType = (echostarContentType) => get(echostarContentTypeMap, [echostarContentType, 'type']);

  const getDropdownProps = (mapping, flaggedMetadata) => ({
    className: classnames('mapping-button--dropdown'),
    children: '',
    displayDirection: 'down',
    dropdownRenderer: () => renderDropdownItems(mapping, flaggedMetadata),
  });

  const handleMapClick = (mapping) => {
    const {
      mappingCompleteMapping,
      mappingSetData,
      massActionData: { active: massActionActive, targets: massActionTargets },
      routerParams: { guid },
      selectTab,
    } = mapping;

    if (echostarContentType) {
      const isShow = echostarContentType === 'SRM';

      mappingSetData(
        {
          mediaItemGuid: guid,
          [getContentType(echostarContentType)]: {
            echostarContentType,
            echostarId,
            genres,
            gnSeriesId,
            rating,
            releaseYear,
            roviProgramId,
            seriesId,
            title,
            tmsId,
            sourceId,
          },
        },
        false,
        !isShow,
      ).then(() => {
        if (isShow) {
          if (!massActionActive || isEmpty(massActionTargets)) {
            selectTab('MOVIESANDPROGRAMSMAPPING');
          } else {
            selectTab('CREATESHELL');
          }
        } else {
          mappingCompleteMapping();
        }
      });
    } else {
      alert('The Echostar Content Type is missing for this record and cannot be mapped.');
    }
  };

  const handleMapAndRememberClick = (mapping) => {
    const {
      mappingSetData,
      mappingSetShowRememberMapping,
      routerParams: { guid },
      selectTab,
    } = mapping;

    if (echostarContentType) {
      const isShow = echostarContentType === 'SRM';

      mappingSetData(
        {
          mediaItemGuid: guid,
          [getContentType(echostarContentType)]: {
            echostarContentType,
            echostarId,
            genres,
            rating,
            releaseYear,
            roviProgramId,
            seriesId,
            title,
            tmsId,
            sourceId,
          },
        },
        false,
        !isShow,
      ).then(() => {
        mappingSetShowRememberMapping(true).then(() => {
          if (isShow) {
            selectTab('MOVIESANDPROGRAMSMAPPING');
          }
        });
      });
    } else {
      alert('The Echostar Content Type is missing for this record and cannot be mapped.');
    }
  };

  const handleCreateCustomShellClick = (mapping) => {
    const { mappingCreateCustomShell, selectTab } = mapping;

    mappingCreateCustomShell({
      description,
      descriptionLanguage,
      echostarContentType,
      echostarId,
      episodeNumber,
      episodeSeason,
      genres,
      gnSeriesId,
      originalAirDate,
      rating,
      releaseYear,
      roviProgramId,
      runtime,
      seriesId,
      seriesTitle,
      title,
      titleLanguages,
    });

    selectTab('CREATESHELL');
  };

  const handleFlagMetadataClick = (mapping, flaggedMetadata) => {
    const { flaggedMetadataSetActive } = flaggedMetadata;

    flaggedMetadataSetActive(
      true,
      roviProgramId,
      servicesDataKey,
      {
        map: () => handleMapClick(mapping),
        createShell: () => handleCreateCustomShellClick(mapping),
      },
      echostarId,
      tmsId,
      sourceId
    );
  };

  const handleClick = (event, name, mapping, flaggedMetadata = {}) => {
    event && event.preventDefault && event.preventDefault();
    if (name === 'map') {
      handleMapClick(mapping);
    } else if (name === 'mapAndRemember') {
      handleMapAndRememberClick(mapping);
    } else if (name === 'createCustomShell') {
      handleCreateCustomShellClick(mapping);
    } else if (name === 'flagMetadata') {
      handleFlagMetadataClick(mapping, flaggedMetadata);
    }
  };

  const shouldRenderButton = ({ franchiseData, massMapping }, { roviProgramId, tmsId }) => !roviProgramId
        && !tmsId
        && !(
          echostarContentType === 'SRE'
            && !isEmpty(franchiseData)
            && franchiseData.echostarContentType === 'SRM'
            && !franchiseData.echostarId
            && !franchiseData.roviProgramId
            && !franchiseData.tmsId
        )
        && !(massMapping && franchiseData.echostarContentType === 'SRM' && echostarContentType !== 'SRE');

  const renderButton = (mapping, flaggedMetadata) => {
    const {
      routerParams: { guid },
    } = mapping;

    return shouldRenderButton(mapping, flaggedMetadata) ? (
            <Permissions
                accessRequirement="write"
                lockAccess={true}
                recordId={guid}
                requiresServiceAccess={true}
                serviceKey="mediaItems"
            >
                {showDropdown() ? (
                    <div className={classnames('mapping-button--div', className)}>
                        <Button {...getButtonProps(mapping)} />
                        {renderDropdown(mapping, flaggedMetadata)}
                    </div>
                ) : (
                    <Button {...getButtonProps(mapping)} />
                )}
            </Permissions>
    ) : null;
  };

  const showDropdown = () => !disabled && (echostarContentType !== 'SRM' || !isShell);

  const renderDropdown = (mapping, flaggedMetadata) => <Dropdown {...getDropdownProps(mapping, flaggedMetadata)} />;

  const renderDropdownItems = (mapping, flaggedMetadata) => {
    const { inSpecialMode } = flaggedMetadata;

    return (
            <ul className="mapping-button--overlay">
                <li>
                    <Button
                        className="mapping-button--overlaybutton"
                        onClick={(event) => handleClick(event, 'map', mapping)}
                    >
                        Map
                    </Button>
                </li>
                {echostarContentType !== 'SRM' ? (
                    <li>
                        <Button
                            className="mapping-button--overlaybutton"
                            onClick={(event) => handleClick(event, 'mapAndRemember', mapping)}
                        >
                            Map And Remember
                        </Button>
                    </li>
                ) : null}
                {!isShell
                && ((mapping.mappingData && mapping.mappingData.franchise) || echostarContentType !== 'SRE') ? (
                    <li>
                        <Button
                            className="mapping-button--overlaybutton"
                            onClick={(event) => handleClick(event, 'createCustomShell', mapping)}
                        >
                            Create Shell from AMS Record
                        </Button>
                    </li>
                  ) : null}
                {!isShell
                && echostarId
                && !inSpecialMode
                && hasAccessByRole({ allowRoles: ['flaggedMetadata_update'] }) ? (
                    <li>
                        <Button
                            className="mapping-button--overlaybutton"
                            onClick={(event) => handleClick(event, 'flagMetadata', mapping, flaggedMetadata)}
                        >
                            Flag Metadata
                        </Button>
                    </li>
                  ) : null}
            </ul>
    );
  };

  return (
        <Mapping>
            {(mapping) => (mapping.routerParams.guid ? (
                    <FlaggedMetadata>{(flaggedMetadata) => renderButton(mapping, flaggedMetadata)}</FlaggedMetadata>
                ) : null)}
        </Mapping>
  );
}

MappingButton.propTypes = {
    className: PropTypes.string,
    description: PropTypes.string,
    descriptionLanguage: PropTypes.string,
    disabled: PropTypes.bool,
    echostarContentType: PropTypes.string,
    echostarId: PropTypes.number,
    episodeNumber: PropTypes.number,
    episodeSeason: PropTypes.number,
    genres: PropTypes.array,
    gnSeriesId: PropTypes.string,
    hasAccessByRole: PropTypes.func.isRequired,
    isShell: PropTypes.bool,
    originalAirDate: PropTypes.string,
    rating: PropTypes.string,
    releaseYear: PropTypes.number,
    roviProgramId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    runtime: PropTypes.number,
    seriesId: PropTypes.number,
    seriesTitle: PropTypes.string,
    servicesDataKey: PropTypes.string,
    sourceId: PropTypes.string,
    title: PropTypes.string.isRequired,
    titleLanguages: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    tmsId: PropTypes.string,
};

export default withPermissions(MappingButton);
